import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Select from "react-select"; // Import react-select
import axiosInstance from "../../interceptor/axiosInstance";
import { toast } from '../Library/SubMenus/MCQAssessment/toastUtils';

const Modal = ({ user, onClose }) => {
  const [series, setSeries] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoad, setSubmitLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const initialized = useRef(false);

  const handleSearchInput = (inputValue) => {
    setSearchTerm(inputValue); // Store search input separately
  }

  // Fetch series data
  const fetchSeries = async (url = "assessments/tests/", page = 1, pageSize = 15, name = "",) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(url, {
        params: url.includes("?") ? {} : { page, page_size: pageSize, name }, // Use nextPage URL as-is if it already has params
      });

      const newOptions = response.data?.data?.results.map((s) => ({
        value: s.id,
        label: s.name,
      }));

      setSeries((prevSeries) => [...prevSeries, ...newOptions]);
      setNextPage(response.data?.data?.pagination?.next); // Store next page URL
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Trigger API Call Whenever searchTerm Changes
  useEffect(() => {
    if (searchTerm.trim() !== "") {
      fetchSeries("assessments/tests/", 1, 15, searchTerm);
    }
  }, [searchTerm]); // ✅ Runs when searchTerm changes


  useEffect(() => {
    if (!initialized.current) {
      fetchSeries();
      initialized.current = true;
    }
  }, []);

  // Handle dropdown change
  const handleSeriesChange = (selectedOption) => {
    if (selectedOption.value === "see-more") {
      if (nextPage) {
        fetchSeries(nextPage); // Fetch next available page dynamically
      }
    } else {
      setSelectedSeriesId(selectedOption.value);
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedSeriesId) {
      toast.error("Error", "Please select an assessment.");
      return;
    }

    setSubmitLoader(true);
    try {
      await axiosInstance.post("assign/user-test-assignment/", {
        user: user.id,
        test: selectedSeriesId,
      });

      toast.success("Success!", "Test assignment was successful.");
      onClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Something went wrong! Please try again later.";
      toast.error("Assignment Failed", errorMessage);
    } finally {
      setSubmitLoader(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="assign-edit-modal-overlay" onClick={onClose}>
      <div className="assign-edit-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="assign-edit-modal-header">
          <div className="assign-edit-modal-title">
            <span className="assign-edit-modal-icon">📝</span>
            <h2>Assign Assessment</h2>
          </div>
          <button className="assign-edit-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="assign-edit-modal-body">
          <div className="assign-edit-user-info">
            <div className="assign-edit-user-label">Assigning to:</div>
            <div className="assign-edit-user-details">
              <span className="assign-edit-user-name">{user.name || "User"}</span>
              <span className="assign-edit-user-email">{user.email}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="assign-edit-modal-form">
            <div className="assign-edit-form-group">
              <label htmlFor="series" className="assign-edit-form-label">
                Select Assessment <span className="assign-edit-required">*</span>
              </label>
              <Select
                id="series"
                options={[
                  ...series,
                  nextPage ? { value: "see-more", label: "📄 Load More..." } : null,
                ].filter(Boolean)}
                onChange={handleSeriesChange}
                isLoading={loading}
                isSearchable
                placeholder="🔍 Search and select an assessment..."
                noOptionsMessage={() => "No assessments found"}
                menuPortalTarget={document.body}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    width: "100%",
                    minHeight: "44px",
                    borderRadius: "8px",
                    border: state.isFocused ? "2px solid #3b82f6" : "2px solid #e2e8f0",
                    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
                    "&:hover": {
                      borderColor: "#3b82f6",
                    },
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 100002 }),
                  menu: (base) => ({ 
                    ...base, 
                    zIndex: 100002,
                    borderRadius: "8px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected 
                      ? "#3b82f6" 
                      : state.isFocused 
                        ? "#eff6ff" 
                        : "white",
                    color: state.isSelected ? "white" : "#1e293b",
                    cursor: "pointer",
                    padding: "10px 12px",
                  }),
                }}
                onInputChange={handleSearchInput}
              />
            </div>

            <div className="assign-edit-modal-footer">
              <button 
                type="button" 
                className="assign-edit-btn-cancel" 
                onClick={onClose}
                disabled={submitLoad}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="assign-edit-btn-submit" 
                disabled={submitLoad || !selectedSeriesId}
              >
                {submitLoad ? (
                  <>
                    <span className="assign-edit-spinner"></span>
                    Assigning...
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    Assign Test
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
